import { makeObservable, observable, action, runInAction } from "mobx";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import { inject, injectable } from "inversify";
import { BudgetRepository } from "@project/data/repositories/budget.repository";
import { EditJustificationTemplateUseCase } from "@project/domain/usecases/budget/edit-justification-template.usecase";
import { FetchJustificationTemplatesUseCase } from "@project/domain/usecases/budget/fetch-justification-templates-usecase";

@injectable()
export class JustificationTemplateEditPageViewModel extends BaseViewModel {
    @observable template: any = null;
    @observable loading: boolean = false;
    @observable saving: boolean = false;

    private generalData: any = {};
    private jsonSchema: any = null;

    constructor(
        @inject(BudgetRepository)
        private readonly budgetRepository: BudgetRepository,
        @inject(EditJustificationTemplateUseCase)
        private readonly editJustificationTemplateUseCase: EditJustificationTemplateUseCase,
        @inject(FetchJustificationTemplatesUseCase)
        private readonly fetchJustificationTemplatesUseCase: FetchJustificationTemplatesUseCase,
    ) {
        super();
        makeObservable(this);
    }

    @action
    async fetchTemplate(id: number) {
        this.loading = true;
        try {
            const templatesRaw = await this.fetchJustificationTemplatesUseCase.execute();
            // Mapear para asegurar que json_schema y template_key están presentes
            const templates = templatesRaw.map((tpl: any) => ({
                id: tpl.id,
                name: tpl.name,
                description: tpl.description,
                version: tpl.version,
                json_schema: tpl.json_schema,
                template_key: tpl.template_key,
            }));
            const found = templates.find((tpl: any) => tpl.id === id);
            runInAction(() => {
                this.template = found;
                this.generalData = found ? { ...found } : {};
                this.jsonSchema = found ? found.json_schema : null;
                this.loading = false;
            });
        } catch (e) {
            runInAction(() => {
                this.loading = false;
            });
        }
    }

    @action
    updateGeneralData(data: any) {
        this.generalData = { ...this.generalData, ...data };
        // Actualizar template para reactividad
        this.template = { ...this.template, ...this.generalData, json_schema: this.jsonSchema };
    }

    @action
    updateJsonSchema(schema: any) {
        this.jsonSchema = schema;
        // Actualizar template para reactividad
        this.template = { ...this.template, ...this.generalData, json_schema: this.jsonSchema };
    }

    @action
    async saveTemplate() {
        if (!this.template) return false;
        this.saving = true;
        try {
            const input = {
                id: this.template.id,
                name: this.generalData.name,
                description: this.generalData.description,
                version: this.generalData.version,
                visible: true,
                json_schema: this.jsonSchema,
                template_key: this.template.template_key,
            };
            const result = await this.editJustificationTemplateUseCase.execute(input);
            runInAction(() => {
                this.saving = false;
            });
            return !!result;
        } catch (e) {
            runInAction(() => {
                this.saving = false;
            });
            return false;
        }
    }
}
